import {useCallback, useEffect, useMemo, useState} from "react";
import {ProfileCustomerNotesLifecycleSelect} from "~/components/entityProfile/notes/profileCustomerNotesLifecycleSelect";
import {CUSTOMER_PROFILE_USE_MOCK_API} from "~/components/entityProfile/customerProfileDataSource";
import type {CustomerProfileNotesContext} from "~/components/entityProfile/customerProfileDataSource/types";
import type {CustomerProfileTabContext} from "~/components/entityProfile/profileTabRegistry";
import type {ProfileTabRenderProps} from "~/components/entityProfile/types";
import {createEmptyComposerId, NotesComposer} from "~/components/notes/notesComposer";
import {ListNotesPanelBody, NotesSortControl} from "~/components/notes/listNotesSidePanel";
import {NotesAddNoteLabel, NotesPreviousNotesLabel} from "~/components/notes/notesPanelTypography";
import {NoteFeedItem} from "~/components/notes/noteFeedItem";
import type {NoteListItem, NoteSortDirection} from "~/domainModels/notes";
import {computeNotePlainTextLength, NOTES_BODY_MAX_PLAIN_LENGTH} from "~/domainModels/notes";
import {AnankeSkeleton} from "@vesta/ananke/components/anankeSkeleton";
import {AnankeText} from "@vesta/ananke/components/anankeText";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";

function ProfileCustomerNotesSkeleton() {
    return (
        <div className="grid gap-ds-space-150">
            <AnankeSkeleton width="100%" height={40} borderRadius="4px" />
            <AnankeSkeleton width="100%" height={120} borderRadius="4px" />
            <AnankeSkeleton width="100%" height={96} borderRadius="4px" />
        </div>
    );
}

function resolveDefaultLifecycleId(
    options: CustomerProfileNotesContext["lifecycleOptions"],
): Uuid | null {
    if (options.length === 0) {
        return null;
    }
    return options[0]?.id ?? null;
}

function ProfileCustomerNotesMockPanel({
    notes,
    selectedLifecycleId,
    onLifecycleChange,
    onDirtyChange,
}: {
    notes: CustomerProfileNotesContext;
    selectedLifecycleId: Uuid | null;
    onLifecycleChange: (next: Uuid) => void;
    onDirtyChange?: (isDirty: boolean) => void;
}) {
    const [sortDirection, setSortDirection] = useState<NoteSortDirection>("recent");
    const [composerMarkdown, setComposerMarkdown] = useState("");
    const [localNotes, setLocalNotes] = useState<Array<NoteListItem>>(notes.items);
    const [composerId] = useState(() => createEmptyComposerId());
    const [composerResetKey, setComposerResetKey] = useState(0);

    useEffect(() => {
        setLocalNotes(notes.items);
    }, [notes.items]);

    const selectedLifecycleLabel = useMemo(() => {
        if (selectedLifecycleId === null) {
            return null;
        }
        return notes.lifecycleOptions.find((option) => option.id === selectedLifecycleId)?.label ?? null;
    }, [notes.lifecycleOptions, selectedLifecycleId]);

    const filteredNotes = useMemo(() => {
        const scoped =
            selectedLifecycleLabel === null
                ? localNotes
                : localNotes.filter((item) => item.sourceLabel === selectedLifecycleLabel);
        const items = [...scoped];
        items.sort((left, right) => {
            const leftMs = Date.parse(left.createdAt);
            const rightMs = Date.parse(right.createdAt);
            return sortDirection === "recent" ? rightMs - leftMs : leftMs - rightMs;
        });
        return items;
    }, [localNotes, selectedLifecycleLabel, sortDirection]);

    const composerPlainLength = computeNotePlainTextLength(composerMarkdown);
    const composerHasDraft = composerPlainLength > 0;
    const composerCanSave =
        selectedLifecycleId !== null
        && composerHasDraft === true
        && composerPlainLength <= NOTES_BODY_MAX_PLAIN_LENGTH;

    useEffect(() => {
        onDirtyChange?.(composerHasDraft);
    }, [composerHasDraft, onDirtyChange]);

    useEffect(() => {
        return () => {
            onDirtyChange?.(false);
        };
    }, [onDirtyChange]);

    const handleSave = useCallback(() => {
        if (composerCanSave !== true || selectedLifecycleId === null || selectedLifecycleLabel === null) {
            return;
        }

        const now = new Date().toISOString() as Iso8601DateTime;
        const nextNote: NoteListItem = {
            id: generateUuid(),
            eventContainerId: selectedLifecycleId,
            bodyMarkdown: composerMarkdown,
            bodyPlainTextLength: composerPlainLength,
            createdBy: generateUuid(),
            createdAt: now,
            updatedAt: now,
            attachments: [],
            scopeType: "eventContainer",
            eventId: null,
            eventTemplateId: null,
            author: {displayName: "You", roleLabel: ""},
            sourceLabel: selectedLifecycleLabel,
        };

        setLocalNotes((previous) => [nextNote, ...previous]);
        setComposerMarkdown("");
        setComposerResetKey((previous) => previous + 1);
    }, [composerCanSave, composerMarkdown, composerPlainLength, selectedLifecycleId, selectedLifecycleLabel]);

    return (
        <div className="grid w-full min-w-0 auto-rows-max content-start gap-ds-space-200">
            <ProfileCustomerNotesLifecycleSelect
                options={notes.lifecycleOptions}
                value={selectedLifecycleId}
                onChange={onLifecycleChange}
                isDisabled={notes.isLoading === true}
            />
            <div className="grid w-full min-w-0 gap-ds-space-050">
                <NotesAddNoteLabel />
                <NotesComposer
                    key={composerResetKey}
                    composerId={composerId}
                    value={composerMarkdown}
                    onChange={setComposerMarkdown}
                    stagedAttachments={[]}
                    onStagedAttachmentsChange={() => {}}
                    isSaving={false}
                    isUploading={false}
                    onUploadFiles={() => {}}
                    onSave={handleSave}
                />
            </div>
            {filteredNotes.length === 0 ? (
                <AnankeText color="color.text.subtlest">No notes yet.</AnankeText>
            ) : null}
            {filteredNotes.length > 0 ? (
                <div className="grid w-full min-w-0 auto-rows-max content-start gap-ds-space-150">
                    <div className="flex w-full min-w-0 items-center justify-between gap-x-ds-space-150">
                        <NotesPreviousNotesLabel />
                        <NotesSortControl sortDirection={sortDirection} onChange={setSortDirection} />
                    </div>
                    {filteredNotes.map((item) => (
                        <NoteFeedItem
                            key={item.id}
                            item={item}
                            canMutate={false}
                            isEditing={false}
                            onStartEdit={() => {}}
                            onCancelEdit={() => {}}
                            editComposer={null}
                            onDelete={() => {}}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
}

export function ProfileCustomerNotesTabContent({
    tabRenderProps,
    context,
    onDirtyChange,
}: {
    tabRenderProps: ProfileTabRenderProps;
    context: CustomerProfileTabContext;
    onDirtyChange?: (isDirty: boolean) => void;
}) {
    const notes = context.notes;
    const [selectedLifecycleId, setSelectedLifecycleId] = useState<Uuid | null>(() =>
        resolveDefaultLifecycleId(notes.lifecycleOptions),
    );

    useEffect(() => {
        if (selectedLifecycleId === null && notes.lifecycleOptions.length > 0) {
            setSelectedLifecycleId(resolveDefaultLifecycleId(notes.lifecycleOptions));
        }
    }, [notes.lifecycleOptions, selectedLifecycleId]);

    const selectedLifecycleLabel = useMemo(() => {
        if (selectedLifecycleId === null) {
            return "Lifecycle";
        }
        return notes.lifecycleOptions.find((option) => option.id === selectedLifecycleId)?.label ?? "Lifecycle";
    }, [notes.lifecycleOptions, selectedLifecycleId]);

    if (tabRenderProps.kind !== "customer") {
        return null;
    }

    const linkedEventContainer = context.customer.eventContainer;
    const useLiveNotesPanel =
        CUSTOMER_PROFILE_USE_MOCK_API !== true && linkedEventContainer !== null;

    return (
        <div className="h-full min-h-0 overflow-y-auto overscroll-contain px-ds-space-200 pb-ds-space-200">
            <div className="grid gap-y-ds-space-200 py-ds-space-150">
                {notes.isLoading === true ? <ProfileCustomerNotesSkeleton /> : null}
                {notes.isLoading !== true && useLiveNotesPanel === true && linkedEventContainer !== null ? (
                    <ListNotesPanelBody
                        context={{
                            eventContainerId: linkedEventContainer.id,
                            eventContainer: linkedEventContainer,
                            createScope: {scopeType: "eventContainer"},
                            eventContainerSourceLabel: selectedLifecycleLabel,
                        }}
                        eventContainer={linkedEventContainer}
                        onEventContainerPatched={() => {}}
                        isFullscreen={false}
                        previousNotesSection={true}
                        lifecycleSelector={{
                            options: notes.lifecycleOptions,
                            value: selectedLifecycleId,
                            onChange: setSelectedLifecycleId,
                        }}
                        onDirtyChange={onDirtyChange}
                    />
                ) : null}
                {notes.isLoading !== true && useLiveNotesPanel !== true ? (
                    <ProfileCustomerNotesMockPanel
                        notes={notes}
                        selectedLifecycleId={selectedLifecycleId}
                        onLifecycleChange={setSelectedLifecycleId}
                        onDirtyChange={onDirtyChange}
                    />
                ) : null}
            </div>
        </div>
    );
}
